import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Users, FileText, CheckSquare, Plus, Clock, MapPin, CheckCircle2, Download, Brain } from 'lucide-react';
import { formatDateLocal, formatLastAnalysisDate, getLocalDateISO } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import Toast from './Toast';
import ConfirmModal from './ConfirmModal';
import { ChecklistTab } from './ChecklistTab';
import type { ComiteReunion, ComiteReunionComplete } from '../services/comiteService';
import { addNote, createReunion, deleteReunion, getReunionComplete, getReunions, updateReunion } from '../services/comiteService';
import { DEFAULT_CLIENT_ID } from '../config/client';
import InfoParticipantsTab from './comiteTi/InfoParticipantsTab';
import AgendaTab from './comiteTi/AgendaTab';
import NotesTab from './comiteTi/NotesTab';
import ActionsTab from './comiteTi/ActionsTab';
import ResumeTab from './comiteTi/ResumeTab';
import { getChecklistItems } from '../services/checklistService';
import { exportComiteReunionPdf } from '../services/comitePdfExporter';
import { getTechPrimesWorkbook, saveTechPrimesWorkbook, type TechPrimesWorkbook } from '../services/techniciensPrimesService';

type TabType = 'info' | 'checklist' | 'agenda' | 'notes' | 'actions' | 'resume' | 'suivi_trimestriel';
const TECH_PRIMES_SUIVI_NOTE_TYPE = 'tech_primes_suivi_snapshot_v1';

function extractWorkbookSnapshotFromNotes(notes: any[] | undefined | null): TechPrimesWorkbook | null {
  if (!Array.isArray(notes)) return null;
  const candidates = notes
    .filter((n) => typeof n?.contenu === 'string' && n.contenu.includes(`"type":"${TECH_PRIMES_SUIVI_NOTE_TYPE}"`))
    .sort((a, b) => {
      const ta = new Date(a?.timestamp || a?.date_creation || 0).getTime();
      const tb = new Date(b?.timestamp || b?.date_creation || 0).getTime();
      return tb - ta;
    });
  for (const c of candidates) {
    try {
      const parsed = JSON.parse(c.contenu);
      if (parsed?.type === TECH_PRIMES_SUIVI_NOTE_TYPE && parsed?.workbook) return parsed.workbook as TechPrimesWorkbook;
    } catch {
      // ignore invalid snapshot payloads
    }
  }
  return null;
}

interface ComiteTIPrismeModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: number;
  title?: string;
  scope?: string;
  defaultReunionType?: string;
}

export default function ComiteTIPrismeModal({ isOpen, onClose, clientId, title, scope, defaultReunionType }: ComiteTIPrismeModalProps) {
  const CLIENT_ID = clientId ?? DEFAULT_CLIENT_ID;
  const COMITE_SCOPE = scope ?? 'CLIENT_TI';
  const DEFAULT_REUNION_TYPE = defaultReunionType ?? 'Comité de Pilotage TI';
  const [reunions, setReunions] = useState<ComiteReunion[]>([]);
  const [selectedReunion, setSelectedReunion] = useState<ComiteReunionComplete | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [showNewReunionForm, setShowNewReunionForm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [confirmDeleteReunion, setConfirmDeleteReunion] = useState(false);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['comite_ti_prisme_last_ai_analysis'] ?? null) : null;

  useEffect(() => {
    if (isOpen) void loadReunions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, CLIENT_ID, COMITE_SCOPE]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  const loadReunions = async () => {
    setLoading(true);
    try {
      const data = await getReunions(CLIENT_ID, COMITE_SCOPE as any);
      setReunions(data);
    } catch (e) {
      console.error(e);
      showToast('Erreur chargement réunions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReunion = async (reunion: ComiteReunion) => {
    if (!reunion.id) return;
    const complete = await getReunionComplete(reunion.id);
    setSelectedReunion(complete);
    setActiveTab('info');
  };

  const handleRefreshReunion = async () => {
    if (!selectedReunion?.id) return;
    const complete = await getReunionComplete(selectedReunion.id);
    setSelectedReunion(complete);
  };

  const handleBackToList = () => {
    setSelectedReunion(null);
    void loadReunions();
  };

  const handleCreateReunion = async (data: Partial<ComiteReunion>) => {
    try {
      // Création de la réunion avec conservation exacte de la date sélectionnée
      const dateReunionOriginal = data.date_reunion;
      const created = await createReunion({ ...data, clientId: CLIENT_ID, comiteScope: COMITE_SCOPE } as any);
      
      // Si la date a été modifiée par le backend, on force la mise à jour avec la date originale
      if (created?.id && dateReunionOriginal && created.date_reunion !== dateReunionOriginal) {
        console.log(`Correction de la date: ${created.date_reunion} -> ${dateReunionOriginal}`);
        await updateReunion(created.id, { date_reunion: dateReunionOriginal });
      }

      if (COMITE_SCOPE === 'TECH_PRIMES' && created?.id) {
        try {
          const reunionsData = await getReunions(CLIENT_ID, COMITE_SCOPE as any);
          const completed = (reunionsData || [])
            .filter((r: any) => r?.id !== created.id && String(r?.statut || '').toLowerCase().includes('compl'))
            .sort((a: any, b: any) => new Date(b?.date_reunion || 0).getTime() - new Date(a?.date_reunion || 0).getTime());

          const lastCompletedId = completed[0]?.id;
          if (lastCompletedId) {
            const lastComplete = await getReunionComplete(lastCompletedId);
            const lastSnapshot = extractWorkbookSnapshotFromNotes(lastComplete?.notes);
            if (lastSnapshot) {
              await addNote({
                reunion_id: created.id,
                type: 'note_generale',
                contenu: JSON.stringify({
                  type: TECH_PRIMES_SUIVI_NOTE_TYPE,
                  sourceReunionId: lastCompletedId,
                  clonedAt: new Date().toISOString(),
                  workbook: lastSnapshot,
                }),
                auteur: 'Système PRIsME',
              } as any);
            }
          }
        } catch (snapshotError) {
          console.error('Erreur clonage suivi trimestriel vers nouvelle réunion TECH_PRIMES:', snapshotError);
        }
      }

      showToast('Réunion créée!', 'success');
      setShowNewReunionForm(false);
      await loadReunions();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la création', 'error');
    }
  };

  const handleDeleteReunion = async () => {
    if (!selectedReunion?.id) return;
    const id = selectedReunion.id;
    try {
      setSelectedReunion(null);
      await deleteReunion(id);
      await loadReunions();
      showToast('Réunion supprimée!', 'success');
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la suppression', 'error');
      await loadReunions();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Toast
        message={toast?.message || ''}
        type={toast?.type || 'success'}
        isVisible={!!toast}
        onClose={() => setToast(null)}
      />

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col"
        >
          <div className="bg-gradient-to-r from-green-600 via-teal-600 to-blue-600 text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-3xl font-bold">{title || '🤝 Comité TI'}</h2>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-white/20 border border-white/30 rounded-full text-xs font-semibold" title="Disponible dans AI Assistant">
                    <Brain className="w-4 h-4 text-yellow-200" />
                    AI
                  </span>
                </div>
                <p className="text-white/90">
                  {selectedReunion
                    ? `Réunion du ${formatDateLocal(selectedReunion.date_reunion, 'fr-CA')}`
                    : `${reunions.length} réunion(s) enregistrée(s)`}
                </p>
                {lastAnalysisDate && (
                  <p className="text-white/70 text-xs mt-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                {!selectedReunion && (
                  <button
                    onClick={() => setShowNewReunionForm(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-white text-green-600 rounded-xl hover:bg-green-50 transition-colors font-bold shadow-lg"
                  >
                    <Plus className="w-5 h-5" />
                    Nouvelle Réunion
                  </button>
                )}

                <button onClick={onClose} className="hover:bg-white/20 rounded-full p-2 transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gradient-to-b from-emerald-50/50 via-slate-50/80 to-slate-100/90 p-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600"></div>
              </div>
            ) : showNewReunionForm ? (
              <NewReunionForm
                onSubmit={handleCreateReunion}
                onCancel={() => setShowNewReunionForm(false)}
                defaultReunionType={DEFAULT_REUNION_TYPE}
              />
            ) : selectedReunion ? (
              <ReunionDetailView
                reunion={selectedReunion}
                comiteScope={COMITE_SCOPE}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onBack={handleBackToList}
                onUpdate={handleRefreshReunion}
                onDelete={() => setConfirmDeleteReunion(true)}
                showToast={showToast}
              />
            ) : (
              <ReunionsList reunions={reunions} onSelect={handleSelectReunion} defaultReunionType={DEFAULT_REUNION_TYPE} />
            )}
          </div>
        </motion.div>
      </div>

      <ConfirmModal
        isOpen={confirmDeleteReunion}
        onClose={() => setConfirmDeleteReunion(false)}
        onConfirm={async () => {
          setConfirmDeleteReunion(false);
          await handleDeleteReunion();
        }}
        title="Supprimer la réunion"
        message="Êtes-vous sûr de vouloir supprimer cette réunion et toutes ses données (participants, agenda, notes, actions, checklist, résumé) ? Cette action est irréversible."
        confirmText="Oui, supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </>
  );
}

// =============================
// List view
// =============================

interface ReunionsListProps {
  reunions: ComiteReunion[];
  onSelect: (reunion: ComiteReunion) => void;
  defaultReunionType?: string;
}

function reunionCounts(r: ComiteReunion): NonNullable<ComiteReunion['counts']> {
  return {
    participants: r.counts?.participants ?? 0,
    agenda: r.counts?.agenda ?? 0,
    notes: r.counts?.notes ?? 0,
    actions: r.counts?.actions ?? 0,
    hasResume: r.counts?.hasResume ?? false,
    checklist: r.counts?.checklist,
  };
}

function ReunionsList({ reunions, onSelect, defaultReunionType = 'Comité TI' }: ReunionsListProps) {
  if (reunions.length === 0) {
    return (
      <div className="text-center py-20">
        <Calendar className="w-20 h-20 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-700 mb-2">Aucune réunion planifiée</h3>
        <p className="text-gray-500">Cliquez sur "Nouvelle Réunion" pour commencer</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {reunions.map((r, idx) => {
        const c = reunionCounts(r);
        const hasChecklist = c.checklist && c.checklist.total > 0;
        const isChecklistValidated = c.checklist?.isValidated === true;
        const borderColor = hasChecklist
          ? isChecklistValidated
            ? 'border-l-4 border-green-600'
            : 'border-l-4 border-yellow-500'
          : 'border-l-4 border-green-500';

        const statut = r.statut || 'Planifiée';
        const isCompleted = statut === 'Complétée';
        const hasResume = c.hasResume === true;

        const timeLabel = r.heure_debut ? `${r.heure_debut}` : '--:--';
        const durationLabel = `${r.duree_prevue_min || 60} min`;

        return (
          <motion.div
            key={r.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            onClick={() => onSelect(r)}
            className={`flex h-full min-h-0 flex-col rounded-xl bg-gradient-to-br from-emerald-50 via-white to-teal-50/90 p-6 shadow-md ring-1 ring-emerald-200/60 transition-all cursor-pointer hover:shadow-xl ${borderColor} hover:scale-[1.02]`}
          >
            <div className="flex min-w-0 items-start justify-between gap-3">
              <div className="min-w-0 flex-1 text-sm text-gray-500 mb-1 break-words">
                {formatDateLocal(r.date_reunion, 'fr-CA', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {isCompleted && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                    Complétée
                  </span>
                )}
                {isChecklistValidated && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-600 text-white">
                    ✓ Validé
                  </span>
                )}
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 break-words leading-snug">{r.type_rencontre || defaultReunionType}</h3>

            <div className="mt-3 space-y-2 text-sm text-gray-700">
              <div className="flex min-w-0 items-center gap-2">
                <Clock className="w-4 h-4 shrink-0 text-gray-500" />
                <span className="min-w-0 break-words">
                  {timeLabel} ({durationLabel})
                </span>
              </div>
              <div className="flex min-w-0 items-start gap-2">
                <MapPin className="w-4 h-4 shrink-0 text-gray-500 mt-0.5" />
                <span className="min-w-0 break-words">{r.lieu || '—'}</span>
              </div>
            </div>

            <div className="mt-4 flex flex-1 flex-col border-t border-gray-200 pt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className={`flex min-w-0 items-center gap-2 ${c.participants > 0 ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                  <Users className="w-4 h-4 shrink-0" />
                  <span className="break-words">{c.participants} participants</span>
                </div>
                <div className={`flex min-w-0 items-center gap-2 ${c.agenda > 0 ? 'text-purple-600 font-medium' : 'text-gray-400'}`}>
                  <FileText className="w-4 h-4 shrink-0" />
                  <span className="break-words">{c.agenda} points</span>
                </div>
                <div className={`flex min-w-0 items-center gap-2 ${c.notes > 0 ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                  <FileText className="w-4 h-4 shrink-0" />
                  <span className="break-words">
                    {c.notes} note{c.notes === 1 ? '' : 's'}
                  </span>
                </div>
                <div className={`flex min-w-0 items-center gap-2 ${c.actions > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                  <CheckSquare className="w-4 h-4 shrink-0" />
                  <span className="break-words">
                    {c.actions} action{c.actions === 1 ? '' : 's'}
                  </span>
                </div>
              </div>

              <div className="mt-3 text-center text-xs text-gray-500">
                {hasResume ? 'Résumé généré' : 'Résumé non généré'}
              </div>

              {hasChecklist && (
                <div
                  className={`mt-4 rounded-lg px-4 py-3 border flex items-center justify-center gap-2 text-sm font-semibold ${
                    isChecklistValidated
                      ? 'bg-green-50 border-green-500 text-green-800'
                      : 'bg-yellow-50 border-yellow-500 text-yellow-900'
                  }`}
                >
                  {isChecklistValidated ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 shrink-0" />
                      Checklist complètement validée
                    </>
                  ) : (
                    <>
                      <CheckSquare className="w-5 h-5 shrink-0" />
                      Checklist à valider ({c.checklist?.validatedMandatory ?? 0}/{c.checklist?.mandatory ?? 0})
                    </>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

// =============================
// New reunion form
// =============================

interface NewReunionFormProps {
  onSubmit: (data: Partial<ComiteReunion>) => void;
  onCancel: () => void;
  defaultReunionType: string;
}

function NewReunionForm({ onSubmit, onCancel, defaultReunionType }: NewReunionFormProps) {
  const [formData, setFormData] = useState<Partial<ComiteReunion>>({
    date_reunion: getLocalDateISO(),
    type_rencontre: defaultReunionType,
    statut: 'Planifiée',
    duree_prevue_min: 60,
    heure_debut: '',
    lieu: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h3 className="text-2xl font-bold text-gray-800 mb-6">Nouvelle Réunion de Comité</h3>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la réunion *</label>
          <input
            type="text"
            required
            value={formData.type_rencontre || ''}
            onChange={(e) => setFormData({ ...formData, type_rencontre: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
            <input
              type="date"
              required
              value={(formData.date_reunion as any) || ''}
              onChange={(e) => setFormData({ ...formData, date_reunion: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Heure de début</label>
            <input
              type="time"
              value={(formData.heure_debut as any) || ''}
              onChange={(e) => setFormData({ ...formData, heure_debut: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Durée prévue (min)</label>
            <input
              type="number"
              min="15"
              step="15"
              value={formData.duree_prevue_min || 60}
              onChange={(e) => setFormData({ ...formData, duree_prevue_min: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Lieu</label>
            <input
              type="text"
              value={(formData.lieu as any) || ''}
              onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Salle de conférence, Teams, etc."
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Objectif principal</label>
          <textarea
            rows={3}
            value={formData.objectif_principal || ''}
            onChange={(e) => setFormData({ ...formData, objectif_principal: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>

        <div className="flex items-center gap-4 pt-4">
          <button type="submit" className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-bold">
            <Plus className="w-5 h-5 inline mr-2" />
            Créer la Réunion
          </button>
          <button type="button" onClick={onCancel} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}

// =============================
// Detail view with tabs
// =============================

interface ReunionDetailViewProps {
  reunion: ComiteReunionComplete;
  comiteScope?: string;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  onBack: () => void;
  onUpdate: () => void;
  onDelete: () => void;
  showToast: (message: string, type: 'success' | 'error') => void;
}

function ReunionDetailView({
  reunion,
  comiteScope,
  activeTab,
  onTabChange,
  onBack,
  onUpdate,
  onDelete,
  showToast,
}: ReunionDetailViewProps) {
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const isTechPrimesScope = comiteScope === 'TECH_PRIMES';
  const tabs = [
    { id: 'info' as TabType, label: 'Info & Participants', icon: Users },
    { id: 'checklist' as TabType, label: 'Checklist', icon: CheckSquare },
    { id: 'agenda' as TabType, label: 'Ordre du Jour', icon: FileText },
    { id: 'notes' as TabType, label: 'Notes', icon: FileText },
    { id: 'actions' as TabType, label: 'Actions', icon: CheckSquare },
    { id: 'resume' as TabType, label: 'Résumé', icon: FileText },
    ...(isTechPrimesScope ? [{ id: 'suivi_trimestriel' as TabType, label: 'Suivi trimestriel', icon: FileText }] : []),
  ];

  const handleExportPdf = async () => {
    if (!reunion.id) {
      showToast('Impossible: réunion sans identifiant', 'error');
      return;
    }

    setIsExportingPdf(true);
    try {
      const checklistItems = await getChecklistItems(reunion.id).catch(() => []);
      await exportComiteReunionPdf({ reunion, checklistItems, comiteScope });
      showToast('PDF exporté', 'success');
    } catch (e) {
      console.error(e);
      showToast(e instanceof Error ? e.message : 'Erreur export PDF', 'error');
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors">
          ← Retour à la liste
        </button>

        <button
          onClick={() => void handleExportPdf()}
          disabled={isExportingPdf}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
            isExportingPdf ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          <Download className="w-4 h-4" />
          {isExportingPdf ? 'Export PDF…' : 'Exporter PDF'}
        </button>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                isActive ? 'bg-green-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-5 h-5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8 min-h-[400px]">
        <AnimatePresence mode="wait">
          {activeTab === 'info' && (
            <motion.div key="info" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <InfoParticipantsTab reunion={reunion} onUpdate={onUpdate} onDelete={onDelete} showToast={showToast} />
            </motion.div>
          )}

          {activeTab === 'checklist' && reunion.id && (
            <motion.div key="checklist" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <ChecklistTab reunionId={reunion.id} onUpdate={onUpdate} showToast={showToast} />
            </motion.div>
          )}

          {activeTab === 'agenda' && (
            <motion.div key="agenda" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <AgendaTab reunion={reunion} onUpdate={onUpdate} showToast={showToast} />
            </motion.div>
          )}

          {activeTab === 'notes' && (
            <motion.div key="notes" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <NotesTab reunion={reunion} onUpdate={onUpdate} showToast={showToast} />
            </motion.div>
          )}

          {activeTab === 'actions' && (
            <motion.div key="actions" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <ActionsTab reunion={reunion} onUpdate={onUpdate} showToast={showToast} />
            </motion.div>
          )}

          {activeTab === 'resume' && (
            <motion.div key="resume" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <ResumeTab reunion={reunion} onUpdate={onUpdate} showToast={showToast} />
            </motion.div>
          )}

          {activeTab === 'suivi_trimestriel' && isTechPrimesScope && (
            <motion.div key="suivi_trimestriel" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              {reunion.id ? (
                <SuiviTrimestrielTab
                  reunionId={reunion.id}
                  reunionDate={String(reunion.date_reunion || '')}
                  clientId={reunion.client_id || DEFAULT_CLIENT_ID}
                  showToast={showToast}
                />
              ) : (
                <div className="text-gray-500">Identifiant de réunion manquant.</div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function SuiviTrimestrielTab({
  reunionId,
  reunionDate,
  clientId,
  showToast,
}: {
  reunionId: number;
  reunionDate: string;
  clientId: number;
  showToast: (message: string, type: 'success' | 'error') => void;
}) {
  const EMPTY_WORKBOOK: TechPrimesWorkbook = { version: 1, tabsOrder: [], tabs: {} };
  const FIXED_TABS = ['Fevrier', 'Mai', 'Juin', 'Aout', 'Octobre', 'Décembre'];
  const CLIENT_COL_INDEX = 0;
  const buildEmptyTab = (name: string) => ({
    name,
    maxCols: 25,
    headerRow: new Array(25).fill(''),
    subHeaderRow: new Array(25).fill(''),
    rows: [] as Array<{ id: string; cells: string[] }>,
  });
  const normalizeWorkbook = (wb: TechPrimesWorkbook | null | undefined): TechPrimesWorkbook => {
    const input = wb && typeof wb === 'object' ? wb : EMPTY_WORKBOOK;
    const sanitizedInputTabsOrder = (Array.isArray(input.tabsOrder) ? input.tabsOrder : [])
      .filter((name) => String(name || '').trim() !== '' && String(name).toLowerCase() !== 'quoi on parle');
    const dynamicTabs = sanitizedInputTabsOrder.filter((name) => !FIXED_TABS.includes(name));
    const finalTabsOrder = [...FIXED_TABS, ...dynamicTabs];

    const nextTabs: Record<string, any> = {};
    for (const tabName of finalTabsOrder) {
      const t = input.tabs?.[tabName];
      if (!t) {
        nextTabs[tabName] = buildEmptyTab(tabName);
        continue;
      }
      const maxCols = Number(t.maxCols || 25);
      const headerRow = Array.isArray(t.headerRow) ? [...t.headerRow] : new Array(maxCols).fill('');
      const subHeaderRow = Array.isArray(t.subHeaderRow) ? [...t.subHeaderRow] : new Array(maxCols).fill('');
      while (headerRow.length < maxCols) headerRow.push('');
      while (subHeaderRow.length < maxCols) subHeaderRow.push('');
      const rows = Array.isArray(t.rows)
        ? t.rows.map((r: any, idx: number) => {
            const cells = Array.isArray(r?.cells) ? [...r.cells] : [];
            while (cells.length < maxCols) cells.push('');
            return { id: r?.id || `${tabName}-${idx + 1}`, cells };
          })
        : [];
      nextTabs[tabName] = { name: tabName, maxCols, headerRow, subHeaderRow, rows };
    }
    return {
      version: Number(input.version || 1),
      tabsOrder: finalTabsOrder,
      tabs: nextTabs,
    };
  };

  const sortRowsByClient = (rows: Array<{ id: string; cells: string[] }>) => {
    const empty: Array<{ id: string; cells: string[] }> = [];
    const filled: Array<{ id: string; cells: string[] }> = [];
    for (const r of rows || []) {
      const client = String(r?.cells?.[CLIENT_COL_INDEX] || '').trim();
      if (!client) empty.push(r);
      else filled.push(r);
    }
    const normalizeSortKey = (v: string) => v.trim().replace(/^[^A-Za-zÀ-ÿ]+/g, '').toLowerCase();
    filled.sort((a, b) => {
      const aRaw = String(a?.cells?.[CLIENT_COL_INDEX] || '');
      const bRaw = String(b?.cells?.[CLIENT_COL_INDEX] || '');
      const aKey = normalizeSortKey(aRaw);
      const bKey = normalizeSortKey(bRaw);
      if (!aKey && bKey) return 1;
      if (aKey && !bKey) return -1;
      return aKey.localeCompare(bKey, 'fr-CA', { sensitivity: 'base' });
    });
    return [...filled, ...empty];
  };

  const sortWorkbookClientsAZ = (wb: TechPrimesWorkbook): TechPrimesWorkbook => {
    const nextTabs: Record<string, any> = {};
    for (const tabName of wb.tabsOrder || []) {
      const tab = wb.tabs?.[tabName];
      if (!tab) continue;
      const sortedRows = sortRowsByClient(Array.isArray(tab.rows) ? tab.rows : []).map((r, idx) => ({
        ...r,
        id: `${tabName}-${idx + 1}`,
      }));
      nextTabs[tabName] = { ...tab, rows: sortedRows };
    }
    return { ...wb, tabs: { ...wb.tabs, ...nextTabs } };
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workbook, setWorkbook] = useState<TechPrimesWorkbook>(EMPTY_WORKBOOK);
  const [activeSheet, setActiveSheet] = useState('');
  const [techFilter, setTechFilter] = useState('');
  const [lastEditedSheet, setLastEditedSheet] = useState('');
  const [editableSheets, setEditableSheets] = useState<string[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<string>('');
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const headerRow1Ref = useRef<HTMLTableRowElement>(null);
  const tableDragRef = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const [headerRow1Height, setHeaderRow1Height] = useState(32);
  const HEADER_TH = 'sticky top-0 z-40 border px-2 py-1 text-left whitespace-nowrap bg-gray-50';
  const HEADER_CLIENT_TH =
    'sticky top-0 left-0 z-50 border px-2 py-1 text-left whitespace-nowrap bg-gray-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)] min-w-[140px]';
  const SUBHEADER_TH = 'sticky z-40 border px-2 py-1 text-left whitespace-nowrap bg-gray-100';
  const SUBHEADER_CLIENT_TH =
    'sticky left-0 z-50 border px-2 py-1 text-left whitespace-nowrap bg-gray-100 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)] min-w-[140px]';
  const clientStickyTd = (selected: boolean) =>
    `sticky left-0 z-20 border p-0 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)] min-w-[140px] ${selected ? 'bg-blue-50' : 'bg-white'}`;
  const endTableDrag = () => {
    tableDragRef.current.active = false;
    const el = tableScrollRef.current;
    if (el) {
      el.style.cursor = 'grab';
      el.style.userSelect = '';
    }
  };
  const onTableMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = tableScrollRef.current;
    if (!el || e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest('input, select, button, textarea, a, label')) return;
    tableDragRef.current = { active: true, startX: e.pageX, scrollLeft: el.scrollLeft };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  };
  const onTableMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!tableDragRef.current.active || !tableScrollRef.current) return;
    e.preventDefault();
    const dx = e.pageX - tableDragRef.current.startX;
    tableScrollRef.current.scrollLeft = tableDragRef.current.scrollLeft - dx;
  };
  const frenchMonths = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
  const normalizeName = (s: string) =>
    String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  const getMeetingMonthCandidates = (isoDate: string) => {
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return [];
    const m = frenchMonths[d.getMonth()] || '';
    const y = d.getFullYear();
    return [`${m} ${y}`, m];
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const reunion = await getReunionComplete(reunionId);
        const reunionSnapshot = extractWorkbookSnapshotFromNotes(reunion?.notes);
        if (reunionSnapshot) {
          const wbSnapshot = normalizeWorkbook(reunionSnapshot);
          if (!mounted) return;
          setWorkbook(wbSnapshot);
          const firstTab = wbSnapshot.tabsOrder?.[0] || FIXED_TABS[0];
          setActiveSheet(firstTab);
          setLastEditedSheet(firstTab);
          const candidates = getMeetingMonthCandidates(reunionDate);
          const autoEditable = (wbSnapshot.tabsOrder || []).filter((tab) =>
            candidates.includes(normalizeName(tab))
          );
          setEditableSheets(autoEditable);
          return;
        }

        const data = await getTechPrimesWorkbook(clientId);
        if (!mounted) return;
        const wb = sortWorkbookClientsAZ(normalizeWorkbook(data?.workbook));
        setWorkbook(wb);
        const firstTab = wb.tabsOrder?.[0] || FIXED_TABS[0];
        setActiveSheet(firstTab);
        setLastEditedSheet(firstTab);
        const candidates = getMeetingMonthCandidates(reunionDate);
        const autoEditable = (wb.tabsOrder || []).filter((tab) =>
          candidates.includes(normalizeName(tab))
        );
        setEditableSheets(autoEditable);
      } catch (e) {
        console.error(e);
        if (mounted) showToast('Erreur chargement suivi trimestriel', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [clientId, reunionDate]);

  useEffect(() => {
    const onWindowMouseUp = () => endTableDrag();
    window.addEventListener('mouseup', onWindowMouseUp);
    return () => window.removeEventListener('mouseup', onWindowMouseUp);
  }, []);

  const selectedSheet = workbook.tabs?.[activeSheet];

  useLayoutEffect(() => {
    const measure = () => {
      const h = headerRow1Ref.current?.getBoundingClientRect().height;
      if (h && h > 0) setHeaderRow1Height(Math.ceil(h));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeSheet, selectedSheet?.headerRow, selectedSheet?.subHeaderRow, techFilter]);
  const techColumns = useMemo(() => {
    if (!selectedSheet?.subHeaderRow) return [];
    const out: Array<{ name: string; colIndex: number }> = [];
    for (let i = 8; i <= 16; i++) {
      const name = (selectedSheet.subHeaderRow[i] || '').trim();
      if (name) out.push({ name, colIndex: i });
    }
    return out;
  }, [selectedSheet]);

  const filteredRows = useMemo(() => {
    if (!selectedSheet?.rows) return [];
    if (!techFilter) return selectedSheet.rows;
    const techCol = techColumns.find((t) => t.name === techFilter)?.colIndex;
    if (techCol == null) return selectedSheet.rows;
    return selectedSheet.rows.filter((r) => {
      const v = r.cells?.[techCol];
      return v != null && String(v).trim() !== '';
    });
  }, [selectedSheet, techColumns, techFilter]);

  const updateCell = (rowId: string, colIndex: number, value: string) => {
    if (!selectedSheet) return;
    if (!editableSheets.includes(activeSheet)) return;
    const next: TechPrimesWorkbook = { ...workbook, tabs: { ...workbook.tabs } };
    const tab = { ...next.tabs[activeSheet] };
    tab.rows = (tab.rows || []).map((r) => {
      if (r.id !== rowId) return r;
      const cells = Array.isArray(r.cells) ? [...r.cells] : [];
      while (cells.length < tab.maxCols) cells.push('');
      cells[colIndex] = value;
      return { ...r, cells };
    });
    next.tabs[activeSheet] = tab;
    setWorkbook(next);
    setLastEditedSheet(activeSheet);
  };

  const cloneSheet = (sourceSheetName: string, newMonthName: string) => {
    const source = workbook.tabs?.[sourceSheetName];
    if (!source) return;
    const normalizedMonth = String(newMonthName || '').trim();
    if (!normalizedMonth) return;
    if (workbook.tabs[normalizedMonth]) {
      showToast('Ce mois existe déjà', 'error');
      return;
    }
    const clonedRows = (source.rows || []).map((r, idx) => ({
      id: `${normalizedMonth}-${idx + 1}`,
      cells: Array.isArray(r.cells) ? [...r.cells] : [],
    }));
    const next: TechPrimesWorkbook = {
      ...workbook,
      tabsOrder: [...(workbook.tabsOrder || []), normalizedMonth],
      tabs: {
        ...workbook.tabs,
        [normalizedMonth]: {
          name: normalizedMonth,
          maxCols: source.maxCols || 25,
          headerRow: Array.isArray(source.headerRow) ? [...source.headerRow] : [],
          subHeaderRow: Array.isArray(source.subHeaderRow) ? [...source.subHeaderRow] : [],
          rows: clonedRows,
        },
      },
    };
    setWorkbook(next);
    setActiveSheet(normalizedMonth);
    setLastEditedSheet(normalizedMonth);
    setEditableSheets((prev) => (prev.includes(normalizedMonth) ? prev : [...prev, normalizedMonth]));
  };

  const handleAddMonth = () => {
    const monthName = window.prompt('Nom du nouveau mois (ex: Janvier 2027)');
    if (!monthName) return;
    const source = lastEditedSheet || activeSheet || workbook.tabsOrder?.[0];
    if (!source) {
      showToast('Aucun onglet source à cloner', 'error');
      return;
    }
    cloneSheet(source, monthName);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sortedWorkbook = sortWorkbookClientsAZ(workbook);
      setWorkbook(sortedWorkbook);
      await saveTechPrimesWorkbook(clientId, sortedWorkbook, null);
      await addNote({
        reunion_id: reunionId,
        type: 'note_generale',
        contenu: JSON.stringify({
          type: TECH_PRIMES_SUIVI_NOTE_TYPE,
          savedAt: new Date().toISOString(),
          workbook: sortedWorkbook,
        }),
        auteur: 'Système PRIsME',
      } as any);
      showToast('Suivi trimestriel sauvegardé', 'success');
    } catch (e) {
      console.error(e);
      showToast('Erreur sauvegarde suivi trimestriel', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addRowToActiveSheet = () => {
    if (!selectedSheet || !editableSheets.includes(activeSheet)) return;
    const next: TechPrimesWorkbook = { ...workbook, tabs: { ...workbook.tabs } };
    const tab = { ...next.tabs[activeSheet] };
    const maxCols = tab.maxCols || 25;
    const newRow = { id: `${activeSheet}-new-${Date.now()}`, cells: new Array(maxCols).fill('') };
    tab.rows = [...(tab.rows || []), newRow];
    next.tabs[activeSheet] = tab;
    setWorkbook(next);
  };

  const removeLastRowFromActiveSheet = () => {
    if (!selectedSheet || !editableSheets.includes(activeSheet)) return;
    const next: TechPrimesWorkbook = { ...workbook, tabs: { ...workbook.tabs } };
    const tab = { ...next.tabs[activeSheet] };
    tab.rows = (tab.rows || []).slice(0, -1);
    next.tabs[activeSheet] = tab;
    setWorkbook(next);
  };

  const removeSelectedRowFromActiveSheet = () => {
    if (!selectedSheet || !editableSheets.includes(activeSheet) || !selectedRowId) return;
    const next: TechPrimesWorkbook = { ...workbook, tabs: { ...workbook.tabs } };
    const tab = { ...next.tabs[activeSheet] };
    tab.rows = (tab.rows || []).filter((r) => r.id !== selectedRowId);
    next.tabs[activeSheet] = tab;
    setWorkbook(next);
    setSelectedRowId('');
  };

  if (loading) {
    return <div className="text-gray-500">Chargement du suivi trimestriel...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500">
        {editableSheets.includes(activeSheet)
          ? `Mode edition actif pour: ${activeSheet}`
          : 'Mode archive en consultation (lecture seule). Cloner un mois pour editer.'}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={() => void handleSave()}
          disabled={saving}
          className={`px-4 py-2 rounded-lg text-white font-semibold ${saving ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
        <button onClick={handleAddMonth} className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700">
          Ajouter un mois (cloner)
        </button>
        {editableSheets.includes(activeSheet) && (
          <>
            <button onClick={addRowToActiveSheet} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
              Ajouter une ligne
            </button>
            <button onClick={removeLastRowFromActiveSheet} className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700">
              Enlever derniere ligne
            </button>
            <button
              onClick={removeSelectedRowFromActiveSheet}
              disabled={!selectedRowId}
              className={`px-4 py-2 rounded-lg text-white font-semibold ${selectedRowId ? 'bg-red-700 hover:bg-red-800' : 'bg-gray-400 cursor-not-allowed'}`}
            >
              Supprimer ligne selectionnee
            </button>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {(workbook.tabsOrder || []).map((tabName) => (
          <button
            key={tabName}
            onClick={() => setActiveSheet(tabName)}
            className={`px-3 py-1 rounded-lg text-sm ${activeSheet === tabName ? 'bg-indigo-600 text-white' : 'bg-gray-100'}`}
          >
            {tabName}
          </button>
        ))}
        {techColumns.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-500">Filtre tech:</span>
            <select className="border rounded px-2 py-1 text-sm" value={techFilter} onChange={(e) => setTechFilter(e.target.value)}>
              <option value="">Tous les techs</option>
              {techColumns.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedSheet ? (
        <div
          ref={tableScrollRef}
          className="overflow-auto border rounded-xl cursor-grab max-h-[min(70vh,720px)]"
          title="Maintenir le clic et glisser pour défiler; les en-têtes restent visibles en haut"
          onMouseDown={onTableMouseDown}
          onMouseMove={onTableMouseMove}
          onMouseUp={endTableDrag}
          onMouseLeave={endTableDrag}
        >
          <table className="min-w-full text-xs border-separate border-spacing-0">
            <thead>
              {selectedSheet.headerRow?.length > 0 && (
                <tr ref={headerRow1Ref} className="bg-gray-50">
                  {selectedSheet.headerRow.map((h, idx) => (
                    <th
                      key={`h-${idx}`}
                      className={idx === CLIENT_COL_INDEX ? HEADER_CLIENT_TH : HEADER_TH}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              )}
              {selectedSheet.subHeaderRow?.length > 0 && (
                <tr className="bg-gray-100">
                  {selectedSheet.subHeaderRow.map((h, idx) => (
                    <th
                      key={`s-${idx}`}
                      className={idx === CLIENT_COL_INDEX ? SUBHEADER_CLIENT_TH : SUBHEADER_TH}
                      style={{ top: headerRow1Height }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const rowSelected = selectedRowId === r.id;
                return (
                  <tr
                    key={r.id}
                    className={rowSelected ? 'bg-blue-50' : ''}
                    onClick={() => {
                      if (!editableSheets.includes(activeSheet)) return;
                      setSelectedRowId(r.id);
                    }}
                  >
                    {(r.cells || []).map((cell, idx) => (
                      <td
                        key={`${r.id}-${idx}`}
                        className={idx === CLIENT_COL_INDEX ? clientStickyTd(rowSelected) : 'border p-0'}
                      >
                        <input
                          className={`w-full px-2 py-1 ${idx === CLIENT_COL_INDEX ? 'min-w-[140px] font-bold' : 'min-w-[120px]'} ${editableSheets.includes(activeSheet) ? (idx === CLIENT_COL_INDEX ? (rowSelected ? 'bg-blue-50' : 'bg-white') : '') : 'bg-gray-50 text-gray-600'}`}
                          value={cell || ''}
                          readOnly={!editableSheets.includes(activeSheet)}
                          onChange={(e) => updateCell(r.id, idx, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-gray-500">Aucun tableau de suivi disponible.</div>
      )}
    </div>
  );
}

